import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AuthContext } from "../state/AuthContext";

export default function AppNavbar() {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const dropdownRef = useRef(null);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = (name) => {
    setActiveDropdown((prev) => (prev === name ? null : name));
  };

  const homePath = user ? "/dashboard" : "/customer";
  const isAdmin = user && user.role === "admin";
  const isTeamMember = user && (user.role === "team_member" || user.role === "admin");

  // Check if active route is under master or entry
  const isMasterActive = location.pathname.startsWith("/master");
  const isEntryActive = location.pathname.startsWith("/entry");

  return (
    <>
      <nav className="app-top-navbar sticky-top">
        <div className="container-fluid d-flex align-items-center justify-content-between">
          
          {/* LEFT: BRAND LOGO & DESKTOP NAVIGATION */}
          <div className="d-flex align-items-center gap-3" ref={dropdownRef}>
            <Link className="d-flex align-items-center gap-2 text-decoration-none me-2" to={homePath}>
              <span className="brand-badge">MK</span>
              <span className="brand-text">MOTORS <span className="fs-6 opacity-75 fw-normal">RTO CRM</span></span>
            </Link>

            {user ? (
              <div className="d-none d-lg-flex align-items-center gap-1">
                {/* Dashboard Link */}
                <NavLink className={({ isActive }) => `top-nav-link ${isActive ? "active" : ""}`} to="/dashboard">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h9a1.5 1.5 0 0 0 1.5-1.5V8.207l.646.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293L8.707 1.5Z"/>
                    <path d="m8 3.293 6 6V13.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V9.293l6-6Z"/>
                  </svg>
                  Dashboard
                </NavLink>

                {/* Master Dropdown (Admin only) */}
                {isAdmin && (
                  <div className="nav-item-dropdown">
                    <button
                      type="button"
                      className={`top-nav-link ${isMasterActive || activeDropdown === "master" ? "active" : ""}`}
                      onClick={() => toggleDropdown("master")}
                    >
                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/>
                      </svg>
                      Master
                      <span className="ms-1 fs-7">▾</span>
                    </button>

                    {activeDropdown === "master" && (
                      <div className="top-dropdown-menu">
                        <NavLink className="top-dropdown-item" to="/master/work-types">
                          Work Type Master <span>→</span>
                        </NavLink>
                        <NavLink className="top-dropdown-item" to="/master/works">
                          Work Master <span>→</span>
                        </NavLink>
                        <NavLink className="top-dropdown-item" to="/master/enquiry-statuses">
                          Enquiry Status <span>→</span>
                        </NavLink>
                        <NavLink className="top-dropdown-item" to="/master/users">
                          Users Master <span>→</span>
                        </NavLink>
                      </div>
                    )}
                  </div>
                )}

                {/* Entry Dropdown (Admin & Team Member) */}
                {isTeamMember && (
                  <div className="nav-item-dropdown">
                    <button
                      type="button"
                      className={`top-nav-link ${isEntryActive || activeDropdown === "entry" ? "active" : ""}`}
                      onClick={() => toggleDropdown("entry")}
                    >
                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z"/>
                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                      </svg>
                      Entry
                      <span className="ms-1 fs-7">▾</span>
                    </button>

                    {activeDropdown === "entry" && (
                      <div className="top-dropdown-menu">
                        <NavLink className="top-dropdown-item" to="/entry/new-lead">
                          New Lead <span>➕</span>
                        </NavLink>
                        <NavLink className="top-dropdown-item" to="/entry/follow-up">
                          Follow Up <span>📋</span>
                        </NavLink>
                        <NavLink className="top-dropdown-item" to="/entry/all-leads">
                          All Leads <span>📁</span>
                        </NavLink>
                        <NavLink className="top-dropdown-item" to="/entry/whatsapp-reminders">
                          WhatsApp Reminders <span>💬</span>
                        </NavLink>
                        <NavLink className="top-dropdown-item" to="/entry/birthday-reminders">
                          Birthday Reminders <span>🎂</span>
                        </NavLink>
                        {isAdmin && (
                          <NavLink className="top-dropdown-item" to="/entry/pending-balances">
                            Pending Balances <span>💰</span>
                          </NavLink>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Report Link */}
                <NavLink className={({ isActive }) => `top-nav-link ${isActive ? "active" : ""}`} to="/reports">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M0 0h1v15h15v1H0V0Zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.75l-3.712 3.712a.5.5 0 1 1-.708-.707l4-4a.5.5 0 0 1 .708 0l2.589 2.588 4.173-5.101a.5.5 0 0 1 .707-.129Z"/>
                  </svg>
                  Report
                </NavLink>

                {/* Settings Link (Admin only) */}
                {isAdmin && (
                  <NavLink className={({ isActive }) => `top-nav-link ${isActive ? "active" : ""}`} to="/settings">
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
                      <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319z"/>
                    </svg>
                    Settings
                  </NavLink>
                )}

                {/* Customer Form Link */}
                <NavLink className={({ isActive }) => `top-nav-link ${isActive ? "active" : ""}`} to="/customer">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                  </svg>
                  Customer Form
                </NavLink>
              </div>
            ) : null}
          </div>

          {/* RIGHT: USER PROFILE & ACTIONS / LOGIN */}
          <div className="d-flex align-items-center gap-2">
            {!user ? (
              <div className="d-flex align-items-center gap-2">
                <NavLink className="btn btn-outline-light btn-sm" to="/customer">
                  Customer Form
                </NavLink>
                <NavLink className="btn btn-primary btn-sm" to="/login">
                  Login
                </NavLink>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-3">
                <div className="user-pill d-none d-sm-flex align-items-center gap-2">
                  <span className="fw-semibold text-white">{user.name}</span>
                  <span className={isAdmin ? "role-badge-admin" : "role-badge-team"}>
                    {isAdmin ? "Admin" : "Team Member"}
                  </span>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-light btn-sm d-flex align-items-center gap-1"
                  onClick={logout}
                  title="Logout"
                >
                  <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 1h-8A1.5 1.5 0 0 0 0 2.5v9A1.5 1.5 0 0 0 1.5 13h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0v2z"/>
                    <path fillRule="evenodd" d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"/>
                  </svg>
                  Logout
                </button>

                {/* Mobile Toggle Button */}
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm d-lg-none"
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                  aria-label="Toggle Navigation"
                >
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    {mobileMenuOpen ? (
                      <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                    ) : (
                      <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
                    )}
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* MOBILE MENU OVERLAY / DRAWER */}
      {user && mobileMenuOpen && (
        <div className="mobile-nav-menu d-lg-none">
          <div className="d-flex align-items-center justify-content-between mb-3 px-2">
            <span className="text-white-50 small">
              Logged in as <strong className="text-white">{user.name}</strong> ({isAdmin ? "Admin" : "Team Member"})
            </span>
          </div>

          <NavLink className="mobile-nav-link" to="/dashboard">
            📊 Dashboard
          </NavLink>

          {isAdmin && (
            <>
              <div className="mobile-section-header">Master</div>
              <NavLink className="mobile-nav-link" to="/master/work-types">Work Type Master</NavLink>
              <NavLink className="mobile-nav-link" to="/master/works">Work Master</NavLink>
              <NavLink className="mobile-nav-link" to="/master/enquiry-statuses">Enquiry Status</NavLink>
              <NavLink className="mobile-nav-link" to="/master/users">Users Master</NavLink>
            </>
          )}

          {isTeamMember && (
            <>
              <div className="mobile-section-header">Entry</div>
              <NavLink className="mobile-nav-link" to="/entry/new-lead">➕ New Lead</NavLink>
              <NavLink className="mobile-nav-link" to="/entry/follow-up">📋 Follow Up</NavLink>
              <NavLink className="mobile-nav-link" to="/entry/all-leads">📁 All Leads</NavLink>
              <NavLink className="mobile-nav-link" to="/entry/whatsapp-reminders">💬 WhatsApp Reminders</NavLink>
              <NavLink className="mobile-nav-link" to="/entry/birthday-reminders">🎂 Birthday Reminders</NavLink>
              {isAdmin && (
                <NavLink className="mobile-nav-link" to="/entry/pending-balances">💰 Pending Balances</NavLink>
              )}
            </>
          )}

          <div className="mobile-section-header">General</div>
          <NavLink className="mobile-nav-link" to="/reports">📈 Report</NavLink>
          {isAdmin && <NavLink className="mobile-nav-link" to="/settings">⚙️ Settings</NavLink>}
          <NavLink className="mobile-nav-link" to="/customer">📋 Customer Form</NavLink>
        </div>
      )}
    </>
  );
}
